import { useLingui } from '@lingui/react/macro';
import { Minus, Plus } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@worldcoo/ui/components/accordion';
import { DiamondGrid, diamondGlows } from '../primitives';

/** An answer is either a paragraph or the hand-off's bulleted list of requirements. */
export type FaqItem = { question: string; answer: string; bullets?: string[] };

/**
 * The tiny filled heart the hand-off uses as a list bullet (8px, `vuesax/linear/heart`). Inlined
 * rather than shipped as a file — it is one path, and inlining lets it take the list's own colour.
 */
function HeartBullet() {
	return (
		<svg viewBox="0 0 8 8" className="mt-[0.45em] size-2 shrink-0" fill="currentColor" aria-hidden>
			<path d="M4 7.2C3.8 7.2 3.6 7.13 3.45 7L1.2 5.2C0.45 4.6 0 3.7 0 2.75 0 1.23 1.1 0 2.45 0c.6 0 1.15.24 1.55.63C4.4.24 4.95 0 5.55 0 6.9 0 8 1.23 8 2.75c0 .95-.45 1.85-1.2 2.45L4.55 7c-.15.13-.35.2-.55.2Z" />
		</svg>
	);
}

/**
 * The page's four questions.
 *
 * Q1's answer is the designer's own, taken from the `pregunta` component in the UI Kit (`45:814`),
 * which is the one item the hand-off draws open. The other three are drawn CLOSED, so the design
 * specifies their questions and none of their answers — those are drafted from how the process
 * actually works and are marked for the content owner to approve (TEC-3626).
 *
 * A hook rather than a module constant because the strings are translated, and it is exported so the
 * section and the page's `FAQPage` structured data are built from ONE array. Answering a question in
 * markup that the page does not show, or vice versa, is exactly the drift that gets a rich result
 * pulled.
 */
export function useFaqItems(): FaqItem[] {
	const { t } = useLingui();
	return [
		{
			question: t`What requirements does Worldcoo set for an NGO that wants to join our partner entities?`,
			answer: t`To join Worldcoo, an organisation has to meet all of the following:`,
			bullets: [
				t`Be recognised as a non-profit organisation.`,
				t`Issue donation certificates valid for tax relief.`,
				t`A track record of at least 3 years since it was constituted.`,
				t`An up-to-date, professional website.`,
				t`Descriptive annual reports on the website.`,
				t`Information about the board of directors or trustees.`,
				t`Publicly available contact details.`,
				t`A data protection policy that complies with current regulations.`,
				t`A presence on social media.`,
				t`Respect for the principles of the Charter of the United Nations.`,
			],
		},
		{
			question: t`How does the project selection process work?`,
			answer: t`Approved projects join our database, and each partner company chooses the ones it wants to fund according to its own values and social strategy. Selection is therefore made by the company, not by Worldcoo — our job is to make sure every project in the pool meets the transparency criteria.`,
		},
		{
			question: t`Once a project is funded, how are the funds raised managed?`,
			answer: t`Donations are collected by the partner company and settled to the NGO through Worldcoo on a regular cycle. You can follow what has been raised in real time from your NGO area, and once the project reaches its goal we transfer the amount and ask for the report showing how it was spent.`,
		},
		{
			question: t`Commitments of the beneficiary organisations`,
			answer: t`Beneficiary entities undertake to use the funds only for the funded project, to report on how they were spent and on the results achieved, and to provide photographs and updates so the people who donated can see the impact of their contribution.`,
		},
	];
}

/**
 * "Preguntas frecuentes" — the linen band that closes the page before the footer CTA.
 *
 * The two diamond-textured glows are the hand-off's own, one bleeding off each side; geometry comes
 * from `diamondGlows`, which encodes the 777x501 ellipse the whole design system builds them from.
 */
export function Faq() {
	const { t } = useLingui();
	const items = useFaqItems();

	return (
		<section className="bg-linen relative overflow-hidden">
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.35}
				masks={[diamondGlows({ x: '6%', y: '0%' }, { x: '94%', y: '78%' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-6 py-16 md:py-[104px]">
				<h2 className="text-center text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Frequently asked questions`}
				</h2>

				<Accordion type="single" collapsible className="flex w-full max-w-[842px] flex-col gap-2">
					{items.map((item) => (
						<AccordionItem
							key={item.question}
							value={item.question}
							className="hover:bg-sand data-[state=open]:bg-sand rounded-2xl border-none px-6 transition-colors lg:px-8"
						>
							{/* The hand-off's marker is a solid orange 32px tile whose plus becomes a MINUS when the
							    item opens — two different glyphs, not one rotated. The Accordion's own pair of
							    chevrons is hidden rather than restyled; they carry
							    `data-slot="accordion-trigger-icon"`, which is also what the component's `ml-auto`
							    rule targets — hence the explicit `flex-1` on the question, without which the tile
							    would sit against the text instead of the far edge. */}
							<AccordionTrigger className="items-center gap-6 py-6 text-left text-lg leading-[1.3] font-medium tracking-[-0.02em] text-black hover:no-underline lg:py-8 lg:text-xl [&_[data-slot=accordion-trigger-icon]]:hidden">
								<span className="flex-1">{item.question}</span>
								<span className="bg-brand grid size-8 shrink-0 place-items-center rounded-lg text-white">
									<Plus className="size-4 group-aria-expanded/accordion-trigger:hidden" />
									<Minus className="hidden size-4 group-aria-expanded/accordion-trigger:block" />
								</span>
							</AccordionTrigger>
							<AccordionContent className="pb-6 lg:pb-8">
								<p className="text-ink/80 text-base leading-[1.5]">{item.answer}</p>
								{item.bullets && (
									<ul className="mt-4 flex flex-col gap-2">
										{item.bullets.map((bullet) => (
											<li key={bullet} className="text-ink flex items-start gap-2 text-base leading-[1.5]">
												<HeartBullet />
												{bullet}
											</li>
										))}
									</ul>
								)}
							</AccordionContent>
						</AccordionItem>
					))}
				</Accordion>
			</div>
		</section>
	);
}
